import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import CaisseController from './controllers/caisse-controller';
import DepotController from './controllers/depot-controller';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';

const fmtMoney = (v) => `${Number(v || 0).toFixed(2)} TND`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

// Default range: last 30 days → today
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const CaisseChiffreAffairePage = () => {
  const { hasPermission } = usePermissionViewModel('caisse');
  const canRead = hasPermission('read');

  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [depotId, setDepotId] = useState('');
  const [depots, setDepots] = useState([]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    DepotController.getAllDepots()
      .then((list) => setDepots(list || []))
      .catch(() => setDepots([]));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await CaisseController.chiffreAffaire({
        from,
        to,
        depotId: depotId || null,
      });
      setData(r);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement du chiffre d’affaires');
    } finally {
      setLoading(false);
    }
  }, [from, to, depotId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const caisses = useMemo(() => data?.caisses || [], [data]);

  if (!canRead) {
    return (
      <div className="p-6 text-center text-gray-500">
        Vous n’avez pas la permission d’accéder à cette page.
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Chiffre d’affaires des caisses</h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Du</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Au</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Dépôt</label>
          <select
            value={depotId}
            onChange={(e) => setDepotId(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 min-w-[180px]"
          >
            <option value="">Tous les dépôts</option>
            {depots.map((d) => (
              <option key={d.id} value={d.id}>{d.name}{d.isDefault ? ' (défaut)' : ''}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => { setFrom(daysAgoISO(30)); setTo(todayISO()); setDepotId(''); }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Réinitialiser
        </button>
        <button
          onClick={fetchData}
          className="px-4 py-2 text-sm font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700"
        >
          Actualiser
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs uppercase text-gray-500">Chiffre d’affaires</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{fmtMoney(data?.totalChiffreAffaire)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs uppercase text-gray-500">Total encaissé</div>
          <div className="mt-1 text-2xl font-bold text-emerald-600">{fmtMoney(data?.totalPaye)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs uppercase text-gray-500">Reste à encaisser</div>
          <div className="mt-1 text-2xl font-bold text-amber-600">{fmtMoney(data?.totalImpaye)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs uppercase text-gray-500">Commandes / Caisses</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">
            {data?.totalCommandes || 0} <span className="text-sm font-medium text-gray-500">/ {data?.nbCaisses || 0}</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Caisse</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Dépôt</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Utilisateur</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ouvert</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fermé</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">État</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Cdes</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">CA</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Encaissé</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Reste</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-500">Chargement…</td></tr>
              ) : caisses.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-500">Aucune donnée sur la période sélectionnée.</td></tr>
              ) : caisses.map((c) => (
                <tr key={c.caisseId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">#{c.caisseId}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{c.depotName || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{(c.utilisateurName || '').trim() || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{fmtDate(c.dateDebut)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{fmtDate(c.dateFin)}</td>
                  <td className="px-4 py-3 text-center">
                    {c.cloture ? (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">Clôturée</span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">Ouverte</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{c.nbCommandes}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{fmtMoney(c.montantTotal)}</td>
                  <td className="px-4 py-3 text-sm text-right text-emerald-700">{fmtMoney(c.montantPaye)}</td>
                  <td className="px-4 py-3 text-sm text-right text-amber-700">{fmtMoney(c.montantImpaye)}</td>
                </tr>
              ))}
            </tbody>
            {caisses.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-sm font-bold text-gray-900 text-right">Total</td>
                  <td className="px-4 py-3 text-sm font-bold text-right text-gray-900">{data?.totalCommandes || 0}</td>
                  <td className="px-4 py-3 text-sm font-bold text-right text-gray-900">{fmtMoney(data?.totalChiffreAffaire)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-right text-emerald-700">{fmtMoney(data?.totalPaye)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-right text-amber-700">{fmtMoney(data?.totalImpaye)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default CaisseChiffreAffairePage;
